import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import DefaultImg from "../../images/DefaultImg.webp";
import { handleProductImageError } from "../../utils/productMedia";
import { useCart } from "../../context/CartContext";
import { useProjectConfig } from "../../context/ProjectConfigContext";
import { useLanding } from "../../context/LandingContext";
import { defaultCatalogReturn } from "../../utils/siteExperienceDefaults";
import { formatMoney } from "../../utils/money";

export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    items,
    subtotal,
    promotionSavings,
    promotions,
    total,
    currency,
    pricingLoading,
    pricingError,
    pricingNotice,
    isPricingReady,
    isEmpty,
    drawerOpen,
    closeCart,
    updateQuantity,
    removeItem,
  } = useCart();
  const { onlinePaymentEnabled, gastronomicMenuEnabled } = useProjectConfig();
  const { navigation } = useLanding();
  const cartUsesCarta = items.some((item) => item?.catalogOrigin === "/carta");
  const catalogReturn = defaultCatalogReturn(
    gastronomicMenuEnabled,
    navigation,
    cartUsesCarta ? "/carta" : "",
  );

  const goTo = (path) => {
    closeCart();
    navigate(path);
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeCart}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 430 },
          maxWidth: "100vw",
          bgcolor: "#fbfbfb",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(13,31,45,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShoppingBagOutlinedIcon sx={{ color: "#E64324" }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#0D1F2D" }}>
              Tu carrito
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar carrito" onClick={closeCart}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {isEmpty ? (
          <Box
            sx={{
              flex: 1,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 2,
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ fontSize: 72, color: "#c7cbd1" }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#0D1F2D" }}>
              El carrito está vacío
            </Typography>
            <Typography sx={{ color: "#6b7280", maxWidth: 300 }}>
              {catalogReturn.path === "/carta"
                ? "Volvé a la carta y agregá lo que quieras pedir."
                : "Agregá productos y volvé cuando estés listo para finalizar la compra."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => goTo(catalogReturn.path)}
              sx={{
                bgcolor: "#E64324",
                fontWeight: 900,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                "&:hover": { bgcolor: "#cc351b" },
              }}
            >
              {catalogReturn.actionLabel}
            </Button>
          </Box>
        ) : (
          <>
            <Stack spacing={0} sx={{ flex: 1, overflowY: "auto", px: 2 }}>
              {items.map((item) => (
                <Box key={item.lineId || item.productId} sx={{ py: 2 }}>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box
                      component="img"
                      src={item.image || DefaultImg}
                      alt={item.name}
                      onError={handleProductImageError}
                      sx={{
                        width: 84,
                        height: 84,
                        objectFit: "contain",
                        bgcolor: "#fff",
                        borderRadius: 2,
                        border: "1px solid rgba(13,31,45,0.08)",
                        flexShrink: 0,
                      }}
                    />

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: "#0D1F2D" }}>
                        {item.name}
                      </Typography>
                      {item.variantLabel ? (
                        <Typography variant="body2" sx={{ color: "#E64324", fontWeight: 850, mt: 0.25 }}>
                          {item.variantLabel}
                        </Typography>
                      ) : null}
                      <Typography sx={{ color: "#2E2E8F", fontWeight: 900, mt: 0.5 }}>
                        {formatMoney(item.price, item.currency || currency)}
                      </Typography>
                      {item.quantityPricing?.appliedTier?.minQuantity ? (
                        <Chip
                          size="small"
                          color="success"
                          label={`Tramo desde ${item.quantityPricing.appliedTier.minQuantity}`}
                          sx={{ mt: 0.7, fontWeight: 800 }}
                        />
                      ) : null}

                      <Box
                        sx={{
                          mt: 1.2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            border: "1px solid rgba(13,31,45,0.16)",
                            borderRadius: 2,
                            bgcolor: "#fff",
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-label={`Quitar una unidad de ${item.name}`}
                            disabled={item.quantity <= 1}
                            onClick={() =>
                              updateQuantity(item.lineId || item.productId, item.quantity - 1)
                            }
                          >
                            <RemoveRoundedIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{ minWidth: 30, textAlign: "center", fontWeight: 900 }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Agregar una unidad de ${item.name}`}
                            disabled={item.stock > 0 && item.quantity >= item.stock}
                            onClick={() =>
                              updateQuantity(item.lineId || item.productId, item.quantity + 1)
                            }
                          >
                            <AddRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <IconButton
                          size="small"
                          aria-label={`Eliminar ${item.name}`}
                          onClick={() => removeItem(item.lineId || item.productId)}
                          sx={{ color: "#E64324" }}
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Stack>

            <Box
              sx={{
                bgcolor: "#fff",
                borderTop: "1px solid rgba(13,31,45,0.08)",
                p: 2.5,
                boxShadow: "0 -8px 24px rgba(13,31,45,0.06)",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ color: "#6b7280" }}>Subtotal</Typography>
                <Typography sx={{ fontWeight: 850 }}>{formatMoney(subtotal, currency)}</Typography>
              </Box>
              {Number(promotionSavings || 0) > 0 ? (
                <Stack spacing={0.45} sx={{ mb: 0.9 }}>
                  {(promotions || []).map((promotion) => (
                    <Box key={`${promotion.promotionId}-${promotion.revision}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: "success.main", fontWeight: 850 }}>{promotion.name}{promotion.groups > 1 ? ` ×${promotion.groups}` : ""}</Typography>
                      <Typography variant="caption" sx={{ color: "success.main", fontWeight: 900 }}>−{formatMoney(promotion.savings, currency)}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography sx={{ fontWeight: 900, color: "#0D1F2D" }}>Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 950, color: "#E64324" }}>{formatMoney(total, currency)}</Typography>
              </Box>

              {pricingNotice ? <Alert severity="info" sx={{ mb: 1.2 }}>{pricingNotice}</Alert> : null}
              {pricingError ? <Alert severity="error" sx={{ mb: 1.2 }}>{pricingError}</Alert> : null}
              <Stack spacing={1.2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => goTo("/checkout")}
                  disabled={pricingLoading || !isPricingReady}
                  sx={{
                    bgcolor: "#E64324",
                    fontWeight: 900,
                    textTransform: "none",
                    borderRadius: 2,
                    py: 1.25,
                    "&:hover": { bgcolor: "#cc351b" },
                  }}
                >
                  {pricingLoading
                    ? "Actualizando precios…"
                    : onlinePaymentEnabled
                      ? "Finalizar compra"
                      : "Enviar pedido"}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => goTo("/carrito")}
                  sx={{
                    color: "#2E2E8F",
                    borderColor: "#2E2E8F",
                    fontWeight: 900,
                    textTransform: "none",
                    borderRadius: 2,
                    py: 1.1,
                  }}
                >
                  Ver carrito completo
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
